import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { auth, onAuthStateChanged, signOut as signOutSession } from '../auth/appAuth';
import {
  consumeAppSmsCode,
  requestAppMagicLink,
  requestAppSmsCode,
} from '../features/two-factor-auth/api/appMagicLinkApi';
import { startUserActivityTracking, stopUserActivityTracking } from '../features/activity-tracking/activityTracker';
import { adminLocationHref } from '../admin-panel/config/adminHost';
import { ADMIN_PATHS } from '../admin-panel/constants/adminPaths';
import {
  readPendingStartedAt,
  writePendingStartedAt,
  readPendingChallengeId,
  writePendingChallengeId,
  readPendingMethod,
  writePendingMethod,
  readPendingDestination,
  writePendingDestination,
  clearPendingSecondFactor,
  generateChallengeId,
  isPendingSecondFactorActive,
  isSecondFactorVerifiedSession,
  markSecondFactorVerifiedSession,
  clearSecondFactorVerifiedSession,
} from '../features/two-factor-auth/utils/pendingSecondFactorSession';
import {
  isImpersonationEntering,
  isImpersonationExiting,
  clearImpersonationSession,
} from '../impersonation/utils/impersonationSession';
import {
  clearRejectedWebsiteAppSession,
  shouldRejectNonOwnerWebsiteSession,
  markWebsiteOwnerSession,
  isWebsiteOwnerSession,
  clearWebsiteOwnerSession,
  canUseWebsiteOwnerPortalAuth,
  isAppRoute,
  notifyWebsitePortalSessionSync,
  notifyAppSecondFactorStarted,
  markAppSecondFactorInProgress,
  clearAppSecondFactorInProgress,
  shouldPreserveWebsitePortalDuringAppAuth,
  isAppSecondFactorInProgress,
  hasWebsiteOwnerPortalMarker,
  WEBSITE_OWNER_UID_STORAGE_KEY,
} from '../website/utils/websiteAuthSession';
import { ROLE_PERMISSIONS } from '../company-management/constants/permissions';
import { fetchCurrentUser } from '../api/me';
import { clearLocalSessionToken, decodeLocalSession, getLocalSessionToken } from '../api/authToken';
import { loginWithPassword } from '../api/localAuth';

const STAFF_ROLES = new Set(['employee', 'manager']);

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

function isImpersonatingUser() {
  const payload = decodeLocalSession(getLocalSessionToken());
  return Boolean(payload?.impersonatorAdminId);
}

function isRateLimitedError(error) {
  if (error?.status === 429) return true;
  const code = error?.code || error?.errorInfo?.code || error?.body?.code || '';
  return String(code).includes('resource-exhausted');
}

async function fetchStaffMeUser(sessionUser) {
  try {
    const token = await sessionUser.getIdToken();
    const payload = await fetchCurrentUser(token);
    return payload?.user || null;
  } catch (error) {
    if (error?.status === 404) return null;
    throw error;
  }
}

function hydrateFromApiPayload(sessionUser, payload) {
  const apiUser = payload?.user || {};
  const userData = {
    ...apiUser,
    uid: sessionUser.uid,
    emailVerified: apiUser.emailVerified === true,
    photoURL: apiUser.photoURL || sessionUser.photoURL || null,
    permissions: Array.isArray(apiUser.permissions) ? apiUser.permissions : [],
    isSpecialAdmin: !!apiUser.isSpecialAdmin,
  };

  if (STAFF_ROLES.has(userData.role)) {
    const roleDefaults = ROLE_PERMISSIONS[userData.role] || [];
    const current = Array.isArray(userData.permissions) ? userData.permissions : [];
    userData.permissions = [...new Set([...current, ...roleDefaults])];
  }

  const company = payload?.company
    ? { id: payload.company.id, ...payload.company }
    : null;

  return {
    user: { ...userData, isSpecialAdmin: !!userData.isSpecialAdmin },
    companyData: company,
  };
}

async function hydrateAuthenticatedUser(sessionUser) {
  const token = await sessionUser.getIdToken();
  const payload = await fetchCurrentUser(token);
  return hydrateFromApiPayload(sessionUser, payload);
}

export const AuthProvider = ({ children }) => {
  const initialPendingSecondFactor = isPendingSecondFactorActive();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [pendingSecondFactorLogin, setPendingSecondFactorLogin] = useState(initialPendingSecondFactor);
  const unsubscribeRef = useRef(null);
  const userDocUnsubRef = useRef(null);
  const pendingSecondFactorLoginRef = useRef(initialPendingSecondFactor);
  const rejectedSessionUidRef = useRef(null);
  const websitePortalHydratedUidRef = useRef(null);
  const hydratedUidRef = useRef(null);

  const updatePendingSecondFactorLogin = useCallback((active) => {
    const next = Boolean(active);
    pendingSecondFactorLoginRef.current = next;
    setPendingSecondFactorLogin((prev) => (prev === next ? prev : next));
  }, []);

  const lockPendingSecondFactorSilently = useCallback(() => {
    pendingSecondFactorLoginRef.current = true;
  }, []);

  const applyHydratedUser = useCallback(async (sessionUser, options = {}) => {
    const impersonating = isImpersonatingUser();
    if (impersonating) {
      markSecondFactorVerifiedSession(sessionUser.uid);
    }

    const uid = sessionUser.uid;
    const meUser = await fetchStaffMeUser(sessionUser);

    if (meUser?.requireMagicLink === true) {
      if (!impersonating && !isSecondFactorVerifiedSession(sessionUser.uid)) {
        const userRole = meUser?.role;
        const allowWebsiteOwnerBypass =
          canUseWebsiteOwnerPortalAuth(userRole, uid) ||
          shouldPreserveWebsitePortalDuringAppAuth(uid);

        if (!allowWebsiteOwnerBypass) {
          const expectedChallengeId = readPendingChallengeId();
          const verifiedChallengeId = meUser?.lastMagicLinkChallengeId || null;
          const isVerified =
            Boolean(expectedChallengeId) &&
            Boolean(verifiedChallengeId) &&
            verifiedChallengeId === expectedChallengeId;

          if (!isVerified) {
            lockPendingSecondFactorSilently();
            setUser(null);
            setCompanyData(null);
            return null;
          }

          updatePendingSecondFactorLogin(false);
          clearPendingSecondFactor();
        }
      } else {
        updatePendingSecondFactorLogin(false);
        clearPendingSecondFactor();
      }
    } else if (isPendingSecondFactorActive()) {
      updatePendingSecondFactorLogin(false);
      clearPendingSecondFactor();
    }

    const hydrated = await hydrateAuthenticatedUser(sessionUser);

    const authStillMatches = Boolean(
      auth.currentUser && auth.currentUser.uid === sessionUser.uid
    );
    const allowWebsitePortalWithoutLiveAuth =
      options.websiteOwnerSession === true ||
      canUseWebsiteOwnerPortalAuth(hydrated.user?.role, sessionUser.uid) ||
      shouldPreserveWebsitePortalDuringAppAuth(sessionUser.uid);

    // Cross-tab token refresh during app 2FA can briefly drop auth.currentUser in this tab.
    if (!authStillMatches && !allowWebsitePortalWithoutLiveAuth) {
      return null;
    }

    if (shouldRejectNonOwnerWebsiteSession(hydrated.user)) {
      await clearRejectedWebsiteAppSession(auth);
      setUser(null);
      setCompanyData(null);
      setAuthUser(null);
      try { stopUserActivityTracking(); } catch (_) {}
      return null;
    }

    setUser(hydrated.user);
    setCompanyData(hydrated.companyData);
    hydratedUidRef.current = sessionUser.uid;

    if (options.websiteOwnerSession === true || shouldPreserveWebsitePortalDuringAppAuth(sessionUser.uid)) {
      websitePortalHydratedUidRef.current = sessionUser.uid;
    }

    if (options.verifyAppSecondFactor === true) {
      markSecondFactorVerifiedSession(sessionUser.uid);
    }

    try { startUserActivityTracking(sessionUser.uid); } catch (_) {}
    return hydrated;
  }, [updatePendingSecondFactorLogin, lockPendingSecondFactorSilently]);

  const handleImpersonationCleanup = async () => {
    try {
      await auth.signOut();
      clearImpersonationSession();
      setUser(null);
      setCompanyData(null);
      setAuthUser(null);
      updatePendingSecondFactorLogin(false);
      clearPendingSecondFactor();
      clearSecondFactorVerifiedSession();
      window.location.replace(adminLocationHref(ADMIN_PATHS.DASHBOARD));
    } catch (error) {
      console.error('Error during impersonation cleanup:', error);
      window.location.replace(adminLocationHref(ADMIN_PATHS.DASHBOARD));
    }
  };

  const AUTH_LOADING_TIMEOUT_MS = 45000;
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => {
      if (pendingSecondFactorLoginRef.current || isPendingSecondFactorActive()) {
        setLoading(false);
        return;
      }
      if (isImpersonationEntering() || isImpersonationExiting()) {
        return;
      }
      // Session still valid — profile hydration may be slow after refresh.
      if (auth.currentUser) {
        setLoading(false);
        return;
      }
      setUser(null);
      setCompanyData(null);
      setLoading(false);
      try { stopUserActivityTracking(); } catch (_) {}
    }, AUTH_LOADING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    if (!unsubscribeRef.current) {
      unsubscribeRef.current = onAuthStateChanged(auth, async (sessionUser) => {
        try {
          const nextUid = sessionUser?.uid || null;
          if (nextUid && hydratedUidRef.current && hydratedUidRef.current !== nextUid) {
            setLoading(true);
            setUser(null);
            setCompanyData(null);
            setAuthUser(null);
          } else if (isImpersonationEntering() && nextUid && hydratedUidRef.current !== nextUid) {
            setLoading(true);
          }

          if (sessionUser) {
            if (!sessionUser.uid) {
              setUser(null);
              setCompanyData(null);
              setAuthUser(null);
              return;
            }

            const impersonating = isImpersonatingUser();
            if (impersonating) {
              markSecondFactorVerifiedSession(sessionUser.uid);
              updatePendingSecondFactorLogin(false);
              clearPendingSecondFactor();
            }

            const pendingStartedAtFromStorage = readPendingStartedAt();
            const pendingChallengeIdFromStorage = readPendingChallengeId();
            let pendingActive = Boolean(pendingStartedAtFromStorage);

            // A verified 2FA session means login already completed — stale pending keys
            // must not block hydration on refresh.
            if (isSecondFactorVerifiedSession(sessionUser.uid)) {
              if (pendingActive || pendingSecondFactorLoginRef.current) {
                clearPendingSecondFactor();
                updatePendingSecondFactorLogin(false);
              }
              pendingActive = false;
            }

            const syncAuthUserIfAllowed = () => {
              setAuthUser({ uid: sessionUser.uid, email: sessionUser.email || null });
            };

            const userData = await fetchStaffMeUser(sessionUser);
            const userExists = Boolean(userData);

            if (userExists) {
              if (shouldRejectNonOwnerWebsiteSession(userData)) {
                if (rejectedSessionUidRef.current === sessionUser.uid) {
                  setUser(null);
                  setCompanyData(null);
                  setAuthUser(null);
                  return;
                }
                rejectedSessionUidRef.current = sessionUser.uid;
                await clearRejectedWebsiteAppSession(auth);
                setUser(null);
                setCompanyData(null);
                setAuthUser(null);
                try { stopUserActivityTracking(); } catch (_) {}
                return;
              }

              rejectedSessionUidRef.current = null;

              // Keep website portal access while the app tab sends/completes its own 2FA flow.
              if (
                !impersonating &&
                userData.role === 'owner' &&
                shouldPreserveWebsitePortalDuringAppAuth(sessionUser.uid)
              ) {
                markWebsiteOwnerSession(sessionUser.uid);
                await applyHydratedUser(sessionUser, {
                  websiteOwnerSession: true,
                  verifyAppSecondFactor: false,
                });
                syncAuthUserIfAllowed();
                return;
              }

              if ((pendingSecondFactorLoginRef.current || pendingActive) && userData.requireMagicLink !== true) {
                updatePendingSecondFactorLogin(false);
                clearPendingSecondFactor();
                pendingActive = false;
              }

              if ((pendingSecondFactorLoginRef.current || pendingActive) && userData.requireMagicLink === true) {
                if (
                  !isAppRoute() &&
                  userData.role === 'owner' &&
                  shouldPreserveWebsitePortalDuringAppAuth(sessionUser.uid)
                ) {
                  markWebsiteOwnerSession(sessionUser.uid);
                  await applyHydratedUser(sessionUser, {
                    websiteOwnerSession: true,
                    verifyAppSecondFactor: false,
                  });
                  syncAuthUserIfAllowed();
                  return;
                }

                const verifiedChallengeId = userData.lastMagicLinkChallengeId || null;
                const expectedChallengeId = pendingChallengeIdFromStorage || null;
                const isVerified =
                  expectedChallengeId &&
                  verifiedChallengeId &&
                  verifiedChallengeId === expectedChallengeId;

                if (!isVerified) {
                  lockPendingSecondFactorSilently();
                  setUser(null);
                  setCompanyData(null);
                  return;
                }

                updatePendingSecondFactorLogin(false);
                clearPendingSecondFactor();
                markSecondFactorVerifiedSession(sessionUser.uid);
                syncAuthUserIfAllowed();
              } else if (
                !impersonating &&
                userData.requireMagicLink === true &&
                !isSecondFactorVerifiedSession(sessionUser.uid) &&
                !canUseWebsiteOwnerPortalAuth(userData.role, sessionUser.uid) &&
                !shouldPreserveWebsitePortalDuringAppAuth(sessionUser.uid)
              ) {
                lockPendingSecondFactorSilently();
                setUser(null);
                setCompanyData(null);
                return;
              }

              const websiteOwnerNeedsAppSecondFactor =
                !impersonating &&
                userData.role === 'owner' &&
                isWebsiteOwnerSession(sessionUser.uid) &&
                isAppRoute() &&
                !isSecondFactorVerifiedSession(sessionUser.uid);

              if (websiteOwnerNeedsAppSecondFactor) {
                setUser(null);
                setCompanyData(null);
                setAuthUser(null);
                return;
              }

              await applyHydratedUser(sessionUser);
              syncAuthUserIfAllowed();
            } else if (pendingSecondFactorLoginRef.current || pendingActive) {
              lockPendingSecondFactorSilently();
              setUser(null);
              setCompanyData(null);
            } else {
              await applyHydratedUser(sessionUser);
              syncAuthUserIfAllowed();
            }
            } else {
            const localToken = getLocalSessionToken();
            if (localToken) {
              try {
                const payload = await fetchCurrentUser(localToken);
                const hydrated = hydrateFromApiPayload(
                  { uid: payload?.user?.uid, email: payload?.user?.email, photoURL: null },
                  payload
                );
                setUser(hydrated.user);
                setCompanyData(hydrated.companyData);
                setAuthUser({ uid: hydrated.user.uid, email: hydrated.user.email || null });
                markSecondFactorVerifiedSession(hydrated.user.uid);
                hydratedUidRef.current = hydrated.user.uid;
                return;
              } catch {
                clearLocalSessionToken();
              }
            }

            const websitePortalUid =
              typeof window !== 'undefined'
                ? localStorage.getItem(WEBSITE_OWNER_UID_STORAGE_KEY)
                : null;
            const deferWebsiteNullHandling =
              typeof window !== 'undefined' &&
              !isAppRoute() &&
              Boolean(websitePortalUid) &&
              (
                websitePortalHydratedUidRef.current === websitePortalUid ||
                isAppSecondFactorInProgress(websitePortalUid) ||
                hasWebsiteOwnerPortalMarker(websitePortalUid)
              );

            if (deferWebsiteNullHandling) {
              // Ignore transient auth flicker — keep current UI; onAuthStateChanged will restore.
              return;
            }

            websitePortalHydratedUidRef.current = null;
            hydratedUidRef.current = null;

            setUser(null);
            setCompanyData(null);
            setAuthUser(null);
            if (!isPendingSecondFactorActive()) {
              updatePendingSecondFactorLogin(false);
              clearPendingSecondFactor();
              const keepWebsitePortalState =
                typeof window !== 'undefined' &&
                !isAppRoute() &&
                Boolean(localStorage.getItem(WEBSITE_OWNER_UID_STORAGE_KEY));
              if (!keepWebsitePortalState) {
                clearSecondFactorVerifiedSession();
              }
              // Website owner portal session survives app-tab auth flows; cleared on explicit sign-out.
            } else {
              lockPendingSecondFactorSilently();
            }
            try { stopUserActivityTracking(); } catch (_) {}
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          if (
            sessionUser?.uid &&
            !isAppRoute() &&
            shouldPreserveWebsitePortalDuringAppAuth(sessionUser.uid)
          ) {
            return;
          }
          if (pendingSecondFactorLoginRef.current || isPendingSecondFactorActive()) {
            lockPendingSecondFactorSilently();
            setUser(null);
            setCompanyData(null);
          } else if (sessionUser) {
            setUser({
              ...sessionUser,
              permissions: [],
              isSpecialAdmin: false,
              emailVerified: false,
            });
            try { startUserActivityTracking(sessionUser.uid); } catch (_) {}
          }
        } finally {
          const stillEntering = isImpersonationEntering() && (
            !auth.currentUser ||
            hydratedUidRef.current !== auth.currentUser.uid
          );
          // Entering a company wins over a stale "returning to admin" flag.
          // Otherwise profile load finishes and the landing page waits forever.
          if (isImpersonationExiting() && !isImpersonationEntering()) {
            setLoading(true);
          } else if (!stillEntering) {
            setLoading(false);
          }
        }
      });
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [applyHydratedUser, updatePendingSecondFactorLogin]);

  // Listen for 2FA verification without authUser React state (avoids full-tree repaint on password sign-in).
  useEffect(() => {
    const cleanupSnapshot = () => {
      if (userDocUnsubRef.current) {
        try {
          userDocUnsubRef.current();
        } catch {
          // ignore
        }
        userDocUnsubRef.current = null;
      }
    };

    const attachSnapshotIfPending = (uid) => {
      const expectedChallengeId = readPendingChallengeId();
      const pendingActive = Boolean(readPendingStartedAt()) && Boolean(expectedChallengeId);
      if (!pendingActive || !uid) {
        cleanupSnapshot();
        return;
      }

      if (userDocUnsubRef.current) return;

      const pollForVerifiedChallenge = async () => {
        const current = auth.currentUser;
        if (!current) return;
        const latestExpected = readPendingChallengeId();
        if (!latestExpected) return;
        try {
          const meUser = await fetchStaffMeUser(current);
          const verifiedChallengeId = meUser?.lastMagicLinkChallengeId || null;
          if (!verifiedChallengeId || verifiedChallengeId !== latestExpected) return;
          markWebsiteOwnerSession(current.uid);
          markSecondFactorVerifiedSession(current.uid);
          const hydrated = await applyHydratedUser(current);
          if (!hydrated?.user) {
            clearSecondFactorVerifiedSession();
            return;
          }
          setAuthUser({ uid: current.uid, email: current.email || null });
          updatePendingSecondFactorLogin(false);
          clearPendingSecondFactor();
          notifyWebsitePortalSessionSync(current.uid);
        } catch (err) {
          console.error('User 2FA poll error:', err);
        }
      };

      void pollForVerifiedChallenge();
      const intervalId = setInterval(() => {
        void pollForVerifiedChallenge();
      }, 2000);
      userDocUnsubRef.current = () => clearInterval(intervalId);
    };

    attachSnapshotIfPending(auth.currentUser?.uid);

    const unsubAuth = onAuthStateChanged(auth, (sessionUser) => {
      cleanupSnapshot();
      attachSnapshotIfPending(sessionUser?.uid);
    });

    return () => {
      unsubAuth();
      cleanupSnapshot();
    };
  }, [applyHydratedUser, updatePendingSecondFactorLogin]);

  const signInWithSecondFactor = async (email, password) => {
    let sessionUser = null;
    try {
      const local = await loginWithPassword(email, password);
      if (local?.token) {
        sessionUser = auth.currentUser;
      }
    } catch (localError) {
      if (localError?.status !== 401 && localError?.status !== 404) {
        throw localError;
      }
      const error = new Error('Invalid email or password');
      error.code = 'auth/invalid-credential';
      throw error;
    }

    if (!sessionUser?.uid) {
      const error = new Error('Invalid email or password');
      error.code = 'auth/invalid-credential';
      throw error;
    }

    clearPendingSecondFactor();
    updatePendingSecondFactorLogin(false);

    const startedAt = Date.now();
    const challengeId = generateChallengeId();

    return beginSecondFactorForUser({
      email: sessionUser.email || email,
      sessionUser: sessionUser,
      startedAt,
      challengeId,
    });
  };

  const resumeSecondFactorForCurrentUser = async () => {
    const current = auth.currentUser;
    if (!current?.uid) {
      throw new Error('Not signed in');
    }

    const existingChallenge = readPendingChallengeId();
    const alreadyPending = Boolean(existingChallenge) && Boolean(readPendingStartedAt());
    if (alreadyPending) {
      lockPendingSecondFactorSilently();
      return {
        secondFactorSent: true,
        alreadyPending: true,
        secondFactorMethod: readPendingMethod(),
        maskedDestination: readPendingDestination(),
      };
    }

    const startedAt = Date.now();
    lockPendingSecondFactorSilently();
    writePendingStartedAt(startedAt);
    const challengeId = generateChallengeId();
    writePendingChallengeId(challengeId);
    notifyAppSecondFactorStarted(current.uid);

    return beginSecondFactorForUser({
      email: current.email || '',
      sessionUser: current,
      startedAt,
      challengeId,
    });
  };

  async function beginSecondFactorForUser({ email, sessionUser, startedAt, challengeId }) {
    const userData = (await fetchStaffMeUser(sessionUser)) || {};
    const requireMagicLink = userData.requireMagicLink === true;
    const twoFactorMethod = userData.twoFactorMethod === 'sms' ? 'sms' : 'email';

    if (!requireMagicLink) {
      updatePendingSecondFactorLogin(false);
      clearPendingSecondFactor();
      clearAppSecondFactorInProgress();
      await applyHydratedUser(sessionUser, { verifyAppSecondFactor: true });
      if (userData.role === 'owner') {
        notifyWebsitePortalSessionSync(sessionUser.uid);
      }
      return { passwordOnly: true };
    }

    if (twoFactorMethod === 'sms') {
      notifyAppSecondFactorStarted(sessionUser.uid);
      writePendingMethod('sms');
      writePendingStartedAt(startedAt);
      writePendingChallengeId(challengeId);
      return {
        secondFactorSending: true,
        secondFactorMethod: 'sms',
        completeSecondFactorSend: async () => {
          try {
            const smsRes = await requestAppSmsCode({ email, challengeId });
            const masked = smsRes?.maskedDestination || '';
            writePendingDestination(masked);
            return {
              secondFactorSent: true,
              secondFactorMethod: 'sms',
              maskedDestination: masked || null,
            };
          } catch (e) {
            updatePendingSecondFactorLogin(false);
            clearPendingSecondFactor();
            await signOutSession(auth);
            if (isRateLimitedError(e)) {
              throw new Error(e?.message || 'Please wait before requesting another SMS code.');
            }
            throw e;
          }
        },
      };
    }

    notifyAppSecondFactorStarted(sessionUser.uid);
    writePendingMethod('email');
    writePendingDestination('');
    writePendingStartedAt(startedAt);
    writePendingChallengeId(challengeId);
    return {
      secondFactorSending: true,
      secondFactorMethod: 'email',
      completeSecondFactorSend: async () => {
        try {
          await requestAppMagicLink({ email, challengeId });
          return { secondFactorSent: true, secondFactorMethod: 'email' };
        } catch (e) {
          if (isRateLimitedError(e)) {
            return { secondFactorSent: true, secondFactorMethod: 'email', rateLimited: true };
          }
          updatePendingSecondFactorLogin(false);
          clearPendingSecondFactor();
          clearSecondFactorVerifiedSession();
          await signOutSession(auth);
          throw e;
        }
      },
    };
  }

  const verifyPendingSmsCode = async (code) => {
    const challengeId = readPendingChallengeId();
    if (!challengeId) {
      throw new Error('Missing verification challenge. Please sign in again.');
    }
    const normalizedCode = String(code || '').trim();
    if (!/^\d{6}$/.test(normalizedCode)) {
      throw new Error('Code must be 6 digits');
    }
    await consumeAppSmsCode({ challengeId, code: normalizedCode });

    const current = auth.currentUser;
    if (!current) {
      throw new Error('Your sign-in session expired. Please sign in again.');
    }

    // Mark verified before hydration so we do not depend on an immediate /api/me
    // read of lastMagicLinkChallengeId (client cache can lag behind the SMS consume write).
    markWebsiteOwnerSession(current.uid);
    markSecondFactorVerifiedSession(current.uid);
    const hydrated = await applyHydratedUser(current);
    if (!hydrated?.user) {
      clearSecondFactorVerifiedSession();
      throw new Error('Could not complete sign-in. Please try again.');
    }

    setAuthUser({ uid: current.uid, email: current.email || null });
    updatePendingSecondFactorLogin(false);
    clearPendingSecondFactor();
    notifyWebsitePortalSessionSync(current.uid);
    return { success: true };
  };

  const getPendingSecondFactorInfo = useCallback(() => ({
    secondFactorMethod: readPendingMethod(),
    maskedDestination: readPendingDestination(),
  }), []);

  const clearAppAuthSession = useCallback(async () => {
    clearLocalSessionToken();
    await clearRejectedWebsiteAppSession(auth);
    setUser(null);
    setCompanyData(null);
    setAuthUser(null);
    updatePendingSecondFactorLogin(false);
    try { stopUserActivityTracking(); } catch (_) {}
  }, [updatePendingSecondFactorLogin]);

  const clearHydratedAppState = useCallback(() => {
    setUser(null);
    setCompanyData(null);
    setAuthUser(null);
    try { stopUserActivityTracking(); } catch (_) {}
  }, []);

  const value = {
    user,
    companyData,
    loading,
    authUser,
    updateUser: setUser,
    updateCompanyData: setCompanyData,
    handleImpersonationCleanup,
    signInWithSecondFactor,
    resumeSecondFactorForCurrentUser,
    verifyPendingSmsCode,
    getPendingSecondFactorInfo,
    clearAppAuthSession,
    clearHydratedAppState,
    pendingSecondFactorLogin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
